import { useState, useRef } from 'react';
import { api } from '../lib/api';
import { Button, Dialog } from './ui';

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  onUploadComplete: () => void;
  /**
   * DOC2. Whether to run AI extraction after the upload. Defaults to TRUE, which is exactly what
   * this dialog has always done - the property page's own "Upload Document" button relies on it.
   *
   * ⛔⛔ THE PROPERTY DATABASE RECORD PAGE PASSES FALSE, DELIBERATELY. `extractDocument` writes
   * transactions into `master_properties` by ADDRESS MATCHING, so on a database record it would
   * let a filed document rewrite the very record being looked at, from a match nobody confirmed -
   * DOC5's asymmetry at the place a mistake is least visible. **Filing a document is not a request
   * to be re-extracted.** DOC3 is where extraction-on-file gets designed on purpose.
   */
  extractOnUpload?: boolean;
  /** The dialog's heading and submit label. "Upload Document" unless a caller says otherwise. */
  title?: string;
  /**
   * Which table `propertyId` names, which decides the WRITE PATH - not a cosmetic difference.
   * `documents.property_id` is foreign-keyed to `properties`, so a Property Database record's
   * document cannot go through the legacy table at all and is filed directly instead.
   */
  targetKind?: 'deal' | 'master';
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

const documentTypes = [
  { value: 'offering_memorandum', label: 'Offering Memorandum' },
  { value: 'property_profile', label: 'Property Profile (CoStar, Crexi, etc.)' },
  { value: 'title_report', label: 'Title Report' },
  { value: 'comp', label: 'Comparable Sale' },
  { value: 'lease', label: 'Lease Agreement' },
  { value: 'appraisal', label: 'Appraisal' },
  { value: 'environmental_report', label: 'Environmental Report' },
  { value: 'other', label: 'Other' },
];

export default function UploadDocumentModal({
  isOpen,
  onClose,
  propertyId,
  onUploadComplete,
  extractOnUpload = true,
  title = 'Upload Document',
  targetKind = 'deal',
}: UploadDocumentModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [fileBuffer, setFileBuffer] = useState<ArrayBuffer | null>(null);
  const [documentType, setDocumentType] = useState('offering_memorandum');
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const processFile = async (selectedFile: File) => {
    const isPdf =
      selectedFile.type === 'application/pdf' ||
      selectedFile.type === 'application/x-pdf' ||
      selectedFile.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      setError('Please upload a PDF file');
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(`File too large (${formatFileSize(selectedFile.size)}). Maximum size is 50MB.`);
      return;
    }

    setFile(selectedFile);
    setFileBuffer(null);
    setError('');
    setReading(true);

    try {
      const buffer = await selectedFile.arrayBuffer();
      if (buffer.byteLength === 0) {
        setError('File appears to be empty. If it\'s in cloud storage, download it first.');
        setFile(null);
        setReading(false);
        return;
      }
      setFileBuffer(buffer);
    } catch {
      setError('Could not read the file. It may be stored in the cloud.');
      setFile(null);
    } finally {
      setReading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) processFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file || !fileBuffer) return;

    setLoading(true);
    setError('');

    try {
      const payload = { name: file.name, size: file.size, type: file.type, buffer: fileBuffer };

      if (targetKind === 'master') {
        // Straight into `files` + `file_links`. No `documents` row exists, so there is nothing to
        // extract from and nothing to fall back on - which is why this one is allowed to throw.
        await api.uploadMasterPropertyDocument(propertyId, payload, documentType);
        onUploadComplete();
        onClose();
        return;
      }

      const result: any = await api.uploadDocumentWithBuffer(payload, propertyId, documentType);

      // An extraction failure must not lose an upload that already landed: the document is filed
      // either way, and DOC0's panel shows it. Same contract as DOC0a's best-effort filing.
      if (extractOnUpload) {
        try {
          await api.extractDocument(result.document.id);
        } catch (extractErr) {
          console.error('[UploadDocumentModal] extraction failed, the document is still filed:', extractErr);
        }
      }

      onUploadComplete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const resetFile = () => {
    setFile(null);
    setFileBuffer(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    /* OO5d: through the Dialog primitive - it brings its own head, close button and scrim. */
    <Dialog
      open
      onClose={onClose}
      title={title}
      size="lg"
      dismissible={!loading}
      footer={<>
        <Button variant="outline" onClick={onClose} disabled={loading} className="flex-1">
          Cancel
        </Button>
        <Button onClick={handleUpload} disabled={!fileBuffer || loading} isLoading={loading} className="flex-1">
          {/* ⭐ The label READS the behaviour rather than describing it from memory. With
              extraction off, "Upload & Extract" promised an AI step that would not run - found by
              looking at the dialog on a Property Database record, 2026-09-05. */}
          {loading ? 'Processing...' : extractOnUpload ? 'Upload & Extract' : 'Upload'}
        </Button>
      </>}
    >
          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            {/* Document Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Type
              </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                disabled={loading}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
              >
                {documentTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* File Drop Zone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PDF File</label>
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors ${
                  dragActive
                    ? 'border-primary-500 bg-primary-50'
                    : fileBuffer
                    ? 'border-success bg-success-wash'
                    : 'border-gray-300 bg-white'
                }`}
              >
                <div className="space-y-1 text-center">
                  {reading ? (
                    <>
                      <svg className="mx-auto h-10 w-10 text-primary-500 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <p className="text-sm text-gray-600 mt-2">Reading file...</p>
                    </>
                  ) : fileBuffer && file ? (
                    <>
                      <svg className="mx-auto h-10 w-10 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-gray-900 font-medium mt-2">
                        {file.name} ({formatFileSize(file.size)})
                      </p>
                      <button
                        type="button"
                        onClick={resetFile}
                        className="text-xs text-primary-500 hover:text-primary-600 mt-1"
                      >
                        Choose a different file
                      </button>
                    </>
                  ) : (
                    <>
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label className="relative cursor-pointer rounded-md font-medium text-primary-500 hover:text-primary-500">
                          <span>Upload a file</span>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={handleFileChange}
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PDF up to 50MB</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
          </div>
    </Dialog>
  );
}
